import React, { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Button } from "./common/ui/button";
import { Input } from "./common/ui/input";
import { Label } from "./common/ui/label";
import { useToast } from "./common/ui/use-toast";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./common/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./common/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./common/ui/dialog";
import {
  AccountMainContainer,
  AccountDetailsContext,
  AccountDetailsTitle,
  AccountDetailsTitleDescription,
} from "./common/Styles";
import { TableContainer, TableHeading, TableWrapper, Title, TitleDescription } from "./Styles";
import { ClipboardCopyIcon, PlusIcon } from "@radix-ui/react-icons";
import {
  createApiKey,
  getApiKeys,
  getBusinesses,
  getUser,
  revokeApiKey,
} from "../services";
import { format } from "date-fns";

type ApiKeyItem = {
  id: string;
  name: string;
  key_prefix?: string;
  environment: string;
  created_at?: string;
  //webhooks: object[];
};

type MYAPIsProps = {
  businessId?: string;
  hideBusinessSelector?: boolean;
};

export default function MYAPIs({ businessId, hideBusinessSelector = false }: MYAPIsProps = {}) {
  const { toast } = useToast();
  const [selectedBusinessId, setSelectedBusinessId] = useState<string>("");
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [openCreate, setOpenCreate] = useState(false);
  const [newKeyName, setNewKeyName] = useState("");
  const [newKeyMode, setNewKeyMode] = useState<"live" | "test" | "">("");
  const [revokingKey, setRevokingKey] = useState<string | null>(null);
  const [createdKeyValue, setCreatedKeyValue] = useState<string>("");
  const [createdKeyCopied, setCreatedKeyCopied] = useState(false);
  
  // user and businesses
  const { data: user } = useQuery({ queryKey: ["userDetails"], queryFn: getUser });
  const { data: businesses } = useQuery({
    queryKey: ["businessDetails", user?.id],
    queryFn: () => getBusinesses(user?.id),
    enabled: !!user?.id,
  });
  useEffect(() => {
    if (businessId) {
      setSelectedBusinessId(String(businessId));
      return;
    }

    if (businesses?.length && !selectedBusinessId) {
      const preferredBusiness = businesses.find((business: any) => business.is_default) ?? businesses[0];
      setSelectedBusinessId(String(preferredBusiness?.id || ""));
    }
  }, [businessId, businesses, selectedBusinessId]);

  const resolvedBusinessId = String(businessId || selectedBusinessId || "");

  const selectedBusinessRecord = businesses?.find(
    (business: any) => String(business.id) === String(resolvedBusinessId),
  );
  // Only when the server has actually said so. Treating an absent field as
  // "not approved" would block businesses whose payload simply omits it.
  const businessAwaitingApproval = selectedBusinessRecord?.is_approved === false;

  useEffect(() => {
    setPage(1);
  }, [resolvedBusinessId]);

  // keys list
  const {
    data: keysResp,
    isFetching: keysLoading,
    refetch: refetchKeys,
  } = useQuery({
    queryKey: ["apiKeys", resolvedBusinessId, page, pageSize],
    queryFn: () => getApiKeys(resolvedBusinessId, page, pageSize),
    enabled: !!resolvedBusinessId,
  });
  
  const apiKeys: ApiKeyItem[] = keysResp?.apikeys || [];
  const hasNext = Boolean(keysResp?.next);
  const hasPrev = Boolean(keysResp?.previous);
  const totalCount = keysResp?.count || 0;
  const totalPages = useMemo(
    () => (pageSize ? Math.max(1, Math.ceil(totalCount / pageSize)) : 1),
    [totalCount, pageSize],
  );

  // create key
  const createKey = useMutation({
    mutationKey: ["createApiKey"],
    mutationFn: () =>
      createApiKey({
        business_id: resolvedBusinessId,
        name: newKeyName,
        mode: (newKeyMode || "test") as any,
      }),
    onSuccess: async (data: any) => {
      const body = data?.data;
      const keyValue = body?.api_key || body?.key || "";
      setNewKeyName("");
      setNewKeyMode("");
      refetchKeys();
      if (keyValue) {
        setCreatedKeyValue(keyValue);
        setCreatedKeyCopied(false);
      } else {
        setOpenCreate(false);
        toast({ description: "API key created." });
      }
    },
    // The server explains why -- "Business must be approved before creating
    // API keys" and the like. Discarding that left the user with a generic
    // failure and no idea that approval was the thing standing in the way.
    onError: (error: any) =>
      toast({
        variant: "destructive",
        title: "Error",
        description:
          error?.response?.data?.message ||
          error?.message ||
          "Failed to create API key.",
      }),
  });

  const revokeKey = useMutation({
    mutationKey: ["revokeApiKey"],
    mutationFn: ({ keyId }: { keyId: string }) => revokeApiKey(resolvedBusinessId, keyId),
    onMutate: ({ keyId }) => {
      setRevokingKey(String(keyId));
    },
    onSuccess: async () => {
      toast({ description: "API key revoked." });
      await refetchKeys();
    },
    onError: (error: any) =>
      toast({
        variant: "destructive",
        title: "Error",
        description:
          error?.response?.data?.message || error?.message || "Failed to revoke API key.",
      }),
    onSettled: () => {
      setRevokingKey(null);
    },
  });

  return (
    <div>
      <Title>API & Webhooks</Title>
      <TitleDescription>Manage your API keys.</TitleDescription>

      <AccountMainContainer>
        {!hideBusinessSelector && (
          <div style={{ flex: 1, minWidth: 320 }}>
            <AccountDetailsContext>
              <AccountDetailsTitle>Business</AccountDetailsTitle>
              <AccountDetailsTitleDescription>
                Select a business to manage credentials.
              </AccountDetailsTitleDescription>
              <div className="mt-3" style={{ maxWidth: 380 }}>
                <Label className="mb-2 block">Select business</Label>
                <Select
                  value={resolvedBusinessId}
                  onValueChange={(v) => {
                    setSelectedBusinessId(v);
                    setPage(1);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Choose a business" />
                  </SelectTrigger>
                  <SelectContent>
                    {businesses?.map((b: any) => (
                      <SelectItem key={b.id} value={String(b.id)}>
                        {b.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </AccountDetailsContext>
          </div>
        )}

        {/* minWidth: 0 lets this shrink inside the flex row -- without it the
            table's own width floors the column and the row overflows rather
            than the table scrolling. */}
        <div style={{ flex: 2, minWidth: 0, width: "100%" }}>
          {/* 95% by default with no horizontal centring, so the card sat
              inset from the right of its column. Overridden here rather than
              in the shared style, which every other table depends on. The
              heading and table body inside are both 95% centred, so they stay
              aligned with each other. */}
          <TableContainer style={{ width: "100%" }}>
            <TableHeading>
              <div>
                <Title style={{ margin: 0 }}>API Keys</Title>
                <TitleDescription>Keys are hidden for security.</TitleDescription>
              </div>
              <Button onClick={() => setOpenCreate(true)} className="flex items-center">
                <PlusIcon className="mr-2" /> Create API key
              </Button>
            </TableHeading>
            <TableWrapper height="calc(100vh - 500px)">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Mode</TableHead>
                    <TableHead>Created date</TableHead>
                    <TableHead>Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {keysLoading ? (
                    <TableRow>
                      <TableCell colSpan={4} style={{ textAlign: "center", paddingTop: "3rem" }}>
                        Loading...
                      </TableCell>
                    </TableRow>
                  ) : apiKeys.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} style={{ textAlign: "center", paddingTop: "3rem" }}>
                        No API keys yet.
                      </TableCell>
                    </TableRow>
                  ) : (
                    apiKeys
                      ?.sort(
                        (a, b) =>
                          new Date(b?.created_at || "").getTime() - new Date(a?.created_at || "").getTime(),
                      )
                      .map((k) => (
                        <TableRow key={k.id} style={{ fontSize: "0.9rem" }}>
                          <TableCell>{k.name}</TableCell>
                          <TableCell>
                            <span
                              className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-semibold ${
                                k.environment !== "LIVE"
                                  ? "bg-red-100 text-red-700"
                                  : "bg-green-100 text-green-700"
                              }`}
                            >
                              {k.environment}
                            </span>
                          </TableCell>
                          <TableCell>
                            {k.created_at ? format(new Date(k.created_at), "dd-MM-yyyy") : "--"}
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="destructive"
                              className="bg-red-500 text-white hover:bg-red-600 text-xs"
                              disabled={revokeKey.isPending && revokingKey === String(k.id)}
                              onClick={() => revokeKey.mutate({ keyId: String(k.id) })}
                            >
                              {revokeKey.isPending && revokingKey === String(k.id)
                                ? "Revoking..."
                                : "Revoke"}
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                  )}
                </TableBody>
              </Table>
            </TableWrapper>
            <div className="flex justify-center items-center gap-4 py-3">
              <Button
                variant="outline"
                disabled={!hasPrev}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                {"<< Previous"}
              </Button>
              <span className="text-sm">
                Page {page} of {totalPages}
              </span>
              <Button variant="outline" disabled={!hasNext} onClick={() => setPage((p) => p + 1)}>
                {"Next >>"}
              </Button>
              <div className="ml-4 flex items-center gap-2">
                <Label>Page size</Label>
                <Select
                  value={String(pageSize)}
                  onValueChange={(v) => {
                    setPageSize(Number(v));
                    setPage(1);
                  }}
                >
                  <SelectTrigger className="w-[100px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[10, 20, 50].map((s) => (
                      <SelectItem key={s} value={String(s)}>
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </TableContainer>
        </div>
      </AccountMainContainer>

      <Dialog
        open={openCreate}
        onOpenChange={(o) => {
          if (!o) {
            setOpenCreate(false);
            setCreatedKeyValue("");
            setCreatedKeyCopied(false);
            setNewKeyName("");
            setNewKeyMode("");
          }
        }}
      >
        <DialogContent>
          {createdKeyValue ? (
            <>
              <DialogHeader>
                <DialogTitle>Your API Key</DialogTitle>
                <DialogDescription>
                  Copy your API key now. For security, it won't be shown again.
                </DialogDescription>
              </DialogHeader>
              <div className="py-2">
                <div className="flex items-center gap-2 p-3 border rounded-md bg-slate-50">
                  <code className="flex-1 text-sm break-all select-all font-mono">
                    {createdKeyValue}
                  </code>
                  <button
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(createdKeyValue);
                        setCreatedKeyCopied(true);
                        toast({ description: "API key copied to clipboard." });
                      } catch {
                        toast({ variant: "destructive", description: "Copy failed." });
                      }
                    }}
                    className="p-2 rounded-md border hover:bg-slate-100 shrink-0"
                    title="Copy API key"
                  >
                    <ClipboardCopyIcon />
                  </button>
                </div>
                <p className="text-xs text-amber-600 mt-3">
                  Store this key in a secure location. You will not be able to view it again after closing this dialog.
                </p>
              </div>
              <DialogFooter>
                <Button
                  onClick={() => {
                    setOpenCreate(false);
                    setCreatedKeyValue("");
                    setCreatedKeyCopied(false);
                  }}
                >
                  {createdKeyCopied ? "Done" : "Close"}
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>Create API Key</DialogTitle>
                <DialogDescription>Create an API key for the selected business.</DialogDescription>
              </DialogHeader>
              <div className="grid gap-4 py-2">
                <div className="grid gap-2">
                  <Label>Business</Label>
                  <Input
                    value={
                      businesses?.find((b: any) => String(b.id) === String(resolvedBusinessId))
                        ?.name || ""
                    }
                    disabled
                  />
                </div>
                {businessAwaitingApproval && (
                  <p className="text-sm text-amber-600">
                    This business is still awaiting approval. API keys can be created
                    once it has been approved.
                  </p>
                )}
                <div className="grid gap-2">
                  <Label>Key name</Label>
                  <Input
                    placeholder="eg: Production key"
                    value={newKeyName}
                    onChange={(e) => setNewKeyName(e.target.value)}
                  />
                </div>
                <div className="grid gap-2">
                  <Label>Mode</Label>
                  <Select
                    value={newKeyMode || undefined}
                    onValueChange={(v) => setNewKeyMode(v as any)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select mode" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="live">Live</SelectItem>
                      <SelectItem value="test">Test</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setOpenCreate(false)}
                  disabled={createKey.isPending}
                >
                  Cancel
                </Button>
                <Button
                  disabled={
                    !newKeyName || !newKeyMode || createKey.isPending || businessAwaitingApproval
                  }
                  onClick={() => createKey.mutate()}
                >
                  {createKey.isPending ? "Creating..." : "Create"}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
